import { updateControlledInputValue } from '@ipfabric/frontend-shared-assets/utils/react/updateControlledInputValue';
import classNames from 'classnames';
import { type FC, type KeyboardEvent, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../Icon/Icon';
import './TextInput.css';
import { isSafeNumber } from '@ipfabric/frontend-shared-assets/utils/types/isSafeNumber';
import { isNullable } from '@ipfabric/utils/types/isNullable';
import { ClosePlainButton } from '../ClosePlainButton/ClosePlainButton';
import { useAutoFocus } from '../hooks/useAutoFocus';
import { fromInteractiveAtomToIconSize } from '../utils/InteractiveAtomProps';
import { InputAdornment } from './InputAdornment';
import { textInputPaddingClassName } from './TextInput.styles';
import type { TextInputProps } from './TextInput.types';

const isNumberOutOfMinMaxRange = (value: number, min?: number, max?: number) =>
  (!isNullable(min) && value < min) || (!isNullable(max) && value > max);

const isNumberOutOfStepGrid = (value: number, step: number, min = 0) => {
  const stepDecimalPlaces = getDecimalPlaces(step);
  const minDecimalPlaces = getDecimalPlaces(min);
  const decimalPlaces = Math.max(stepDecimalPlaces, minDecimalPlaces);
  const valueDecimalPlaces = getDecimalPlaces(value);
  // if value has more decimal places than step/min, it is out of step grid
  if (valueDecimalPlaces > decimalPlaces) {
    return true;
  }
  const remainder = (value - min) % step;
  const roundedRemainder = Number.parseFloat(remainder.toFixed(decimalPlaces));
  // we need to check both sides of remainder, because of floating point precision.
  // It can be rounded to 0 or to step. Every other value is out of step grid.
  return roundedRemainder !== 0 && roundedRemainder !== step;
};

const getDecimalPlaces = (value: number) => {
  const valueString = value.toString();
  return valueString.split('.')[1]?.length ?? 0;
};

/**
 * Note about type='number': to get consistent behavior with internal functionality (stepper, range checks, etc.),
 * use `+value` when getting the value from the input. Keep in mind that `+'' === 0` when validating for empty value.
 */
export const TextInput: FC<TextInputProps> = ({
  size = 'default',
  value = '',
  ...props
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Complex conditions are necessary for stepper logic
}) => {
  const isClearing = useRef(false);

  const inputRefInternal = useAutoFocus(props.autoFocus, props.inputRef);

  const readOnlyMode =
    props.readOnly ||
    (props.renderTags !== undefined && !props.editableWithTags);

  const showStepper =
    (props.type === 'number' || props.stepper) && !readOnlyMode;
  const stepSize = props.step ?? 1;

  const canShowClosePlainButton =
    (value || props.renderTags) &&
    props.type !== 'number' &&
    !props.disabled &&
    !props.loading;

  const showStartAdornment =
    props.type === 'search' || Boolean(props.startAdornment);

  const adornmentClassName = props.disabled
    ? 'text-status-disabled-dark cursor-default'
    : `${props.dropdownOpened ? 'text-status-selected' : 'text-default'} group-focus-within:text-status-selected cursor-default`;

  const numberIsInvalid = props.type === 'number' && typeof +value !== 'number';

  const numberIsOutOfRange = props.type === 'number' && !isSafeNumber(+value);

  const numberIsOutOfMinMaxRange =
    props.type === 'number' && value !== ''
      ? isNumberOutOfMinMaxRange(+value, props.min, props.max)
      : false;

  const numberIsOutOfStepGrid =
    props.type === 'number' && value !== ''
      ? isNumberOutOfStepGrid(+value, stepSize, props.min)
      : false;

  const performStep = (direction: 'stepUp' | 'stepDown') => {
    if (inputRefInternal.current) {
      const currentValue = +inputRefInternal.current.value || 0;
      const newValue =
        direction === 'stepUp'
          ? currentValue + stepSize
          : currentValue - stepSize;

      // Fix floating point precision issues by rounding to stepSize decimal places
      const stepDecimalPlaces = getDecimalPlaces(stepSize);
      const roundedValue = Number.parseFloat(
        newValue.toFixed(stepDecimalPlaces),
      );

      updateControlledInputValue(
        inputRefInternal.current,
        roundedValue.toString(),
      );
    }
  };

  const stepDownIsDisabled = Boolean(
    props.disabled ||
      Number.isNaN(+value) ||
      (props.type === 'number' &&
        typeof props.min === 'number' &&
        +value <= props.min),
  );

  const stepUpIsDisabled = Boolean(
    props.disabled ||
      Number.isNaN(+value) ||
      (props.type === 'number' &&
        typeof props.max === 'number' &&
        +value >= props.max),
  );

  const handleArrowKey = (
    e: KeyboardEvent<HTMLInputElement>,
    direction: 'stepUp' | 'stepDown',
  ) => {
    e.preventDefault();
    const isDisabled =
      direction === 'stepUp' ? stepUpIsDisabled : stepDownIsDisabled;
    if (!isDisabled) {
      performStep(direction);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Handle arrow keys for number inputs with stepper functionality
    if (props.type === 'number' && showStepper && !props.disabled) {
      if (e.key === 'ArrowUp') {
        handleArrowKey(e, 'stepUp');
      } else if (e.key === 'ArrowDown') {
        handleArrowKey(e, 'stepDown');
      }
    }

    props.onKeyDown?.(e);
  };

  const iconSize = fromInteractiveAtomToIconSize[size];

  return (
    <div
      className={classNames(
        `text-input text-input--${props.type} text-input--${size}`,
        'border subtract-border',
        textInputPaddingClassName[size],
        'group',
        props.className,
        props.renderTags && props.editableWithTags && 'flex-wrap',
        {
          'text-input--disabled': props.disabled,
          'text-input--error':
            props.hasError ||
            numberIsInvalid ||
            numberIsOutOfRange ||
            numberIsOutOfMinMaxRange ||
            numberIsOutOfStepGrid,
          'text-input--with-dropdown': props.hasDropdown,
          'text-input--with-open-dropdown': props.dropdownOpened,
          'text-input--with-open-dropdown-bottom':
            props.dropdownOpened === 'bottom',
          'text-input--with-open-dropdown-top': props.dropdownOpened === 'top',
          'text-input--read-only': props.readOnly,
        },
      )}
      onMouseDown={(e) => {
        if (props.selectionDisabled) {
          e.preventDefault();
        }
        window.setTimeout(() => {
          inputRefInternal.current?.focus();
        });
      }}
      onKeyDown={props.onKeyDown}
      onClick={props.onClick}
    >
      {showStartAdornment && (
        <InputAdornment
          className={adornmentClassName}
          position='start'
          iconSize={iconSize}
        >
          {props.startAdornment ?? <Icon glyph='fa-magnifying-glass' />}
        </InputAdornment>
      )}
      {props.renderTags && (
        <div
          className={twMerge(
            'flex flex-wrap gap-4xs -my-3xs',
            !props.editableWithTags && 'w-full overflow-hidden',
            props.tagsContainerClassName,
          )}
        >
          {props.renderTags()}
        </div>
      )}
      <input
        autoComplete={
          props.autoComplete ?? (props.type === 'password' ? 'off' : undefined)
        }
        data-testid={props.dataTestId}
        disabled={props.disabled}
        value={value}
        onChange={props.onChange}
        type={props.type === 'number' ? 'text' : props.type}
        // For number inputs, use inputMode="numeric" to show numeric keyboard on mobile devices
        // while keeping type="text" to allow invalid intermediate values like "20-"
        inputMode={props.type === 'number' ? 'numeric' : undefined}
        placeholder={
          props.renderTags && !props.editableWithTags
            ? undefined
            : props.placeholder
        }
        ref={inputRefInternal}
        name={props.name}
        id={props.id}
        onFocus={(e) => {
          if (readOnlyMode && props.type !== 'number') {
            e.target.setSelectionRange(0, 0); // prevent text selection, not supported for number input
          }
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          if (!isClearing.current) {
            props.onBlur?.(e);
          }
          isClearing.current = false;
        }}
        onKeyDown={handleKeyDown}
        readOnly={readOnlyMode}
        className={classNames(
          props.renderTags && !props.editableWithTags
            ? 'w-0'
            : props.renderTags && props.editableWithTags
              ? 'flex-1'
              : 'w-full',
          props.inputClassName,
        )}
      />

      {showStepper && (
        <div className='text-input__stepper'>
          <button
            className='text-input__button'
            disabled={stepUpIsDisabled}
            onClick={() => performStep('stepUp')}
            type='button'
          >
            <Icon glyph='fa-chevron-up' size='xs' />
          </button>
          <button
            className='text-input__button'
            disabled={stepDownIsDisabled}
            onClick={() => performStep('stepDown')}
            type='button'
          >
            <Icon glyph='fa-chevron-down' size='xs' />
          </button>
        </div>
      )}
      {props.onClear && canShowClosePlainButton && (
        <ClosePlainButton
          size='small'
          disabled={props.disabled}
          onMouseDown={() => {
            isClearing.current = true;
          }}
          onClick={props.onClear}
        />
      )}
      {props.endAdornment && !props.loading && (
        <InputAdornment
          className={adornmentClassName}
          position='end'
          iconSize={iconSize}
        >
          {props.endAdornment}
        </InputAdornment>
      )}
      {props.loading && (
        <Icon
          glyph='fa-loader'
          loading={props.loading}
          size={iconSize}
          className='group-disabled:text-status-disabled-dark pointer-events-none'
        />
      )}
    </div>
  );
};
